import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { S25ButtonComponent } from "./s25.button.component";
import { WorkflowService } from "../modules/workflow/service/workflow.service";
import { S25Util } from "../util/s25-util";
import { Task } from "../pojo/Task";
import { S25PipesModule } from "../pipes/s25.pipes.module";

@Component({
    selector: "s25-ng-requested-changes",
    standalone: true,
    imports: [CommonModule, S25ButtonComponent, S25PipesModule],
    template: `
        <div class="requested-changes-section">
            <s25-ng-button
                [type]="'outline'"
                [onClick]="toggleRequestedChanges"
                [buttonClass]="'requested-changes-button'"
                [isLoading]="isLoading"
            >
                <i class="fa fa-comment"></i>
                {{ showingChanges ? "Hide" : "View" }} Requested Changes
            </s25-ng-button>

            @if (showingChanges && requestedChangesList?.length) {
                <div class="requested-changes-list">
                    <h5>Requested Changes:</h5>
                    @for (change of requestedChangesList; track change.todoId) {
                        <div class="requested-change-item">
                            <div class="change-meta">
                                <strong>{{ change.lastModDt | dateFormat: "MM/dd/yyyy h:mm a" }}</strong>
                            </div>
                            <div class="change-comment">
                                {{ change?.comment?.commentText }}
                            </div>
                        </div>
                    }
                </div>
            }

            @if (showingChanges && !requestedChangesList?.length) {
                <div class="no-changes">No requested changes found.</div>
            }
        </div>
    `,
    styles: [
        `
            .requested-changes-section {
                margin: 0.75rem 0;
            }

            .requested-changes-button {
                margin-bottom: 0.5rem;
            }

            .requested-changes-list {
                background: #fff3cd;
                border: 1px solid #ffc107;
                border-radius: 6px;
                padding: 1rem;
                margin-top: 0.5rem;
            }

            .requested-changes-list h5 {
                margin: 0 0 0.75rem 0;
                color: #856404;
                font-size: 0.95rem;
                font-weight: 600;
            }

            .requested-change-item {
                background: white;
                padding: 0.75rem;
                border-radius: 4px;
                margin-bottom: 0.75rem;
                border-left: 3px solid #ffc107;
            }

            .requested-change-item:last-child {
                margin-bottom: 0;
            }

            .change-meta {
                margin-bottom: 0.5rem;
                color: #666;
                font-size: 0.875rem;
            }

            .change-meta strong {
                color: #333;
            }

            .change-comment {
                color: #333;
                line-height: 1.5;
                white-space: pre-wrap;
            }

            .no-changes {
                padding: 0.75rem;
                margin-top: 0.5rem;
                background: #f8f9fa;
                border: 1px solid #dee2e6;
                border-radius: 4px;
                color: #6c757d;
                font-style: italic;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class S25RequestedChangesComponent {
    @Input() itemTypeId: number;
    @Input() itemId: number;

    showingChanges = false;
    requestedChangesList?: Task.DBTodo[];
    isLoading = false;

    constructor(private cd: ChangeDetectorRef) {}

    toggleRequestedChanges = async () => {
        try {
            // Toggle the showing state
            if (this.showingChanges) {
                this.showingChanges = false;
                this.requestedChangesList = undefined;
            } else {
                // Fetch requested changes from the server
                this.isLoading = true;
                this.cd.detectChanges();

                const requestedChanges = await WorkflowService.getRequestedChanges(this.itemTypeId, this.itemId);

                this.requestedChangesList = requestedChanges;
                this.showingChanges = true;
            }

            this.cd.detectChanges();
        } catch (error) {
            console.error("Failed to fetch requested changes:", error);
            S25Util.showError("Failed to load requested changes");
        } finally {
            this.isLoading = false;
            this.cd.detectChanges();
        }
    };
}
